import Phaser from 'phaser';
import { COLORS, GAME_W, GAME_H, PlayerStats, intToHex } from '../utils/constants';
import { calculateAnalysis, AnalysisData } from '../utils/scoring';
import { soundManager } from '../systems/SoundManager';
import { authManager } from '../systems/AuthManager';
import { HeadTracker } from '../systems/HeadTracker';
import { saveScore } from '../utils/leaderboard';
import { walletManager } from '../systems/WalletManager';
import { apiFetch } from '../config';

interface BattleResult {
  won: boolean;
  score: number;
  battleTime: number;
  playerStats: PlayerStats;
  challengeStats: { total: number; success: number; fail: number; rate: number };
  hitRate: number;
  bossPhase: number;
  playerName?: string;
  headTracker?: HeadTracker | null;
  keyboardOnly?: boolean;
}

export default class AnalysisScene extends Phaser.Scene {
  private analysis!: AnalysisData;
  private result!: BattleResult;
  private revealIndex = 0;
  private revealCbs: (() => void)[] = [];
  private aiText!: Phaser.GameObjects.Text;
  private aiTextIndex = 0;
  private aiTimer: Phaser.Time.TimerEvent | null = null;
  private promoted = false;

  constructor() {
    super({ key: 'AnalysisScene' });
  }

  init(data: BattleResult) {
    this.result = data;
    this.analysis = calculateAnalysis(
      data.playerStats, data.challengeStats, data.hitRate, data.battleTime, data.won
    );
    this.promoted = this.checkRankPromotion(this.analysis.totalScore);
    this.saveToBackend();
  }

  private checkRankPromotion(newScore: number): boolean {
    try {
      const raw = localStorage.getItem('neuro_duel_leaderboard');
      if (!raw) return true;
      const entries = JSON.parse(raw);
      const prevBest = entries.length > 0 ? entries[0].score : 0;
      return newScore > prevBest;
    } catch { return true; }
  }

  private async saveToBackend() {
    const leaderboardEntry = {
      playerName: authManager.getPlayerName() || 'Player',
      score: this.analysis.totalScore, grade: this.analysis.grade,
      battleTime: this.result.battleTime,
      bossDamage: this.result.playerStats.hitsLanded,
      hitRate: isNaN(this.result.hitRate) ? 0 : this.result.hitRate,
      maxCombo: this.result.playerStats.maxCombo,
      dodgeCount: this.result.playerStats.dodges,
      challengeCompletionRate: this.result.challengeStats.rate,
    };

    saveScore(leaderboardEntry);

    try {
      await apiFetch('/api/battles', {
        method: 'POST',
        body: JSON.stringify({
          player_id: authManager.getPlayerId() || 1,
          score: this.analysis.totalScore, rank: this.analysis.grade,
          battle_time_seconds: this.result.battleTime,
          reaction_speed_score: this.analysis.reactionSpeed,
          body_coordination_score: this.analysis.bodyCoordination,
          combat_efficiency_score: this.analysis.combatEfficiency,
          survival_score: this.analysis.survival,
          attention_score: this.analysis.attentionIndex,
          hit_rate: this.result.hitRate, combo_max: this.result.playerStats.maxCombo,
          dodge_count: this.result.playerStats.dodges,
          damage_dealt: this.result.playerStats.hitsLanded,
          damage_taken: this.result.playerStats.damagesTaken,
          challenge_completion_rate: this.result.challengeStats.rate,
          ai_evaluation: this.analysis.aiComment,
        }),
      });
    } catch (_) {}
  }

  create() {
    this.cameras.main.setBackgroundColor(COLORS.bgDark);

    const grid = this.add.graphics().setDepth(0);
    grid.lineStyle(1, COLORS.cyan, 0.03);
    for (let x = 0; x < GAME_W; x += 40) grid.lineBetween(x, 0, x, GAME_H);
    for (let y = 0; y < GAME_H; y += 40) grid.lineBetween(0, y, GAME_W, y);

    const tc = this.result.won ? COLORS.cyan : COLORS.red;
    this.add.text(GAME_W / 2, 40, this.result.won ? '战斗分析' : '失败分析', {
      fontFamily: 'Microsoft YaHei, Arial Black, Arial, sans-serif',
      fontSize: '36px', color: intToHex(tc),
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(10);

    const gt = this.add.text(GAME_W / 2, 100, this.analysis.grade, {
      fontFamily: 'Arial Black, Arial, sans-serif',
      fontSize: '80px', color: this.gradeColor(this.analysis.grade),
      stroke: '#000000', strokeThickness: 4,
    }).setOrigin(0.5).setDepth(10).setAlpha(0);

    this.tweens.add({ targets: gt, alpha: 1, scaleX: { from: 2, to: 1 }, scaleY: { from: 2, to: 1 }, duration: 800, ease: 'Back.easeOut' });

    this.add.text(GAME_W / 2, 155, `总分: ${this.analysis.totalScore}`, {
      fontFamily: 'Arial', fontSize: '20px', color: '#ffffff',
    }).setOrigin(0.5).setDepth(10);

    const stats = [
      { label: '反应速度', value: this.analysis.reactionSpeed, desc: '挑战完成速度' },
      { label: '身体协调', value: this.analysis.bodyCoordination, desc: '头部追踪精度' },
      { label: '战斗效率', value: this.analysis.combatEfficiency, desc: '命中率与技能运用' },
      { label: '生存能力', value: this.analysis.survival, desc: '伤害回避能力' },
      { label: '注意力指数', value: this.analysis.attentionIndex, desc: '多任务处理能力' },
    ];

    let sy = 210;
    stats.forEach((s, i) => {
      this.revealCbs.push(() => this.animateBar(160, sy + i * 50, s.label, s.value, s.desc));
    });

    const dy = sy + stats.length * 50 + 20;
    const coins = Math.floor(this.analysis.totalScore / 10) + (this.result.won ? 100 : 0);
    const gems = (this.result.won ? 1 : 0) + (this.analysis.totalScore >= 80 ? 1 : 0);

    // 金币奖励 - 大号金色显示
    const coinReward = this.add.text(GAME_W / 2, dy, `💰 +${coins} 金币`, {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '22px',
      color: '#ffea00', fontStyle: 'bold',
      stroke: '#000000', strokeThickness: 3,
    }).setOrigin(0.5).setDepth(10).setAlpha(0);
    this.revealCbs.push(() => {
      this.tweens.add({ targets: coinReward, alpha: 1, y: dy - 5, duration: 400 });
      this.tweens.add({ targets: coinReward, scaleX: 1.2, scaleY: 1.2, duration: 200, yoyo: true, delay: 200 });
    });

    if (gems > 0) {
      const gemReward = this.add.text(GAME_W / 2, dy + 30, `💎 +${gems} 宝石`, {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px',
        color: intToHex(COLORS.cyan), fontStyle: 'bold',
        stroke: '#000000', strokeThickness: 2,
      }).setOrigin(0.5).setDepth(10).setAlpha(0);
      this.revealCbs.push(() => this.tweens.add({ targets: gemReward, alpha: 1, duration: 400, delay: 200 }));
    }

    const details = [
      `战斗时间: ${this.result.battleTime.toFixed(1)}s`,
      `命中率: ${(this.result.hitRate * 100).toFixed(0)}%`,
      `最高连击: ${this.result.playerStats.maxCombo}`,
      `闪避次数: ${this.result.playerStats.dodges}`,
      `挑战完成: ${this.result.challengeStats.success}/${this.result.challengeStats.total}`,
    ];
    const dt = this.add.text(GAME_W / 2, dy + (gems > 0 ? 60 : 35), details.join('  |  '), {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '12px', color: '#555577',
    }).setOrigin(0.5).setDepth(10).setAlpha(0);
    this.revealCbs.push(() => this.tweens.add({ targets: dt, alpha: 1, duration: 400 }));

    const cy2 = dy + (gems > 0 ? 100 : 70);
    this.add.text(GAME_W / 2, cy2, 'AI 分析报告', {
      fontFamily: 'Arial, sans-serif', fontSize: '16px', color: '#9945ff', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(10);

    this.aiText = this.add.text(GAME_W / 2, cy2 + 28, '', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: '#ffffff',
      wordWrap: { width: GAME_W - 250 }, lineSpacing: 6,
    }).setOrigin(0.5, 0).setDepth(10).setAlpha(0);

    this.time.delayedCall(500, () => this.revealNext());

    const by2 = GAME_H - 55;
    this.makeBtn(GAME_W / 2 - 140, by2, '再来一次', COLORS.cyan, () => {
      this.fadeTo('BattleScene', {
        headTracker: this.result.headTracker || null,
        keyboardOnly: this.result.keyboardOnly ?? true,
        playerName: this.result.playerName,
      });
    });
    this.makeBtn(GAME_W / 2 + 140, by2, '返回主菜单', COLORS.purple, () => this.fadeTo('TitleScene'));

    this.input.keyboard!.on('keydown-ENTER', () => this.fadeTo('TitleScene'));
    this.input.keyboard!.on('keydown-R', () => {
      this.fadeTo('BattleScene', {
        headTracker: this.result.headTracker || null,
        keyboardOnly: this.result.keyboardOnly ?? true,
        playerName: this.result.playerName,
      });
    });
  }

  private animateBar(x: number, y: number, label: string, value: number, desc: string) {
    const bw = 300, bh = 20;
    const barX = x, barY = y + 6;
    const col = this.statColorNum(value);

    const stars = value >= 90 ? '★★★' : value >= 70 ? '★★☆' : value >= 45 ? '★☆☆' : '☆☆☆';
    this.add.text(x - 10, y, label, {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '14px', color: '#ffffff', fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(10);
    this.add.text(x - 10, y + 14, stars, {
      fontFamily: 'Arial, sans-serif', fontSize: '10px', color: value >= 70 ? '#ffea00' : '#555577',
    }).setOrigin(1, 0).setDepth(10);
    this.add.text(x - 10, y + 26, desc, {
      fontFamily: 'Arial', fontSize: '10px', color: '#555577',
    }).setOrigin(1, 0).setDepth(10);

    const compArrow = value >= 80 ? '↑↑' : value >= 60 ? '↑' : value >= 40 ? '↓' : '↓↓';
    const compColor = value >= 60 ? '#00e676' : '#ff1744';

    // Neon glow background
    const bg = this.add.graphics().setDepth(10);
    bg.fillStyle(col, 0.08 + Math.sin(Date.now() * 0.002) * 0.04);
    bg.fillRoundedRect(barX - 3, barY - 3, bw + 6, bh + 6, 7);
    bg.fillStyle(COLORS.darkGray, 0.9);
    bg.fillRoundedRect(barX, barY, bw, bh, 4);
    bg.lineStyle(1, col, 0.3);
    bg.strokeRoundedRect(barX, barY, bw, bh, 4);

    const vt = this.add.text(barX + bw + 35, barY - 2, '0', {
      fontFamily: 'Arial', fontSize: '16px', color: this.statColor(value), fontStyle: 'bold',
    }).setOrigin(1, 0).setDepth(10);

    const at = this.add.text(barX + bw + 40, barY - 2, compArrow, {
      fontFamily: 'Arial', fontSize: '14px', color: compColor, fontStyle: 'bold',
    }).setOrigin(0, 0).setDepth(10).setAlpha(0);

    // Animated neon fill
    let fill = 0;
    const bar = this.add.graphics().setDepth(11);
    const step = () => {
      fill = Math.min(fill + 0.025, value / 100);
      bar.clear();

      // Outer glow ring
      if (fill > 0.01) {
        const glowPulse = 0.15 + Math.sin(Date.now() * 0.005 + fill * 10) * 0.08;
        bar.fillStyle(col, glowPulse);
        bar.fillRoundedRect(barX - 2, barY - 2, (bw + 4) * fill, bh + 4, 6);
      }

      // Main fill with gradient
      if (fill > 0.01) {
        // Dark base
        bar.fillStyle(col, 0.9);
        bar.fillRoundedRect(barX + 1, barY + 2, (bw - 2) * fill, bh - 4, 3);

        // Bright center strip
        bar.fillStyle(0xffffff, 0.2);
        bar.fillRect(barX + 3, barY + 4, Math.max(6, (bw - 6) * fill), 4);

        // Animated scan line
        const scanX = barX + ((fill * bw) * (0.3 + Math.sin(Date.now() * 0.008) * 0.3));
        bar.fillStyle(0xffffff, 0.3);
        bar.fillRect(scanX, barY + 2, 4, bh - 4);
        bar.fillStyle(col, 0.5);
        bar.fillRect(scanX - 1, barY + 2, 6, bh - 4);
      }

      vt.setText(Math.round(fill * 100).toString());
      if (fill >= value / 100) {
        this.tweens.add({ targets: at, alpha: 1, duration: 300 });
      }
      if (fill < value / 100) this.time.delayedCall(20, step);
    };
    this.time.delayedCall(200, step);
  }

  private revealNext() {
    if (this.revealIndex >= this.revealCbs.length) {
      this.startAI();
      return;
    }
    this.revealCbs[this.revealIndex]();
    this.revealIndex++;
    this.time.delayedCall(350, () => this.revealNext());
  }

  private startAI() {
    this.tweens.add({ targets: this.aiText, alpha: 1, duration: 300 });
    const txt = this.analysis.aiComment;
    this.aiTextIndex = 0;
    this.aiTimer = this.time.addEvent({
      delay: 30,
      callback: () => {
        this.aiTextIndex++;
        this.aiText.setText(txt.substring(0, this.aiTextIndex));
        if (this.aiTextIndex >= txt.length) {
          this.aiTimer?.remove();
          if (this.promoted) this.showPromotionPopup();
        }
      },
      repeat: txt.length,
    });
  }

  private showPromotionPopup() {
    const cx = GAME_W / 2, cy = GAME_H / 2;
    const pw = 460, ph = 280;

    const overlay = this.add.rectangle(cx, cy, GAME_W, GAME_H, 0x000000, 0.75)
      .setInteractive().setDepth(60);

    const panel = this.add.rectangle(cx, cy, pw, ph, 0x0a0a1a, 0.98)
      .setStrokeStyle(2, COLORS.yellow, 0.9).setDepth(61);

    const glow = this.add.graphics().setDepth(60);
    glow.fillStyle(COLORS.yellow, 0.06);
    glow.fillRoundedRect(cx - pw / 2 - 6, cy - ph / 2 - 6, pw + 12, ph + 12, 16);

    const star = this.add.text(cx, cy - 70, '★', {
      fontFamily: 'Arial, sans-serif', fontSize: '48px', color: '#ffea00',
    }).setOrigin(0.5).setDepth(62);

    this.tweens.add({
      targets: star,
      scaleX: { from: 0.5, to: 1.2 }, scaleY: { from: 0.5, to: 1.2 },
      duration: 500, yoyo: true, repeat: -1,
    });

    this.add.text(cx, cy - 30, '恭喜！新排名！', {
      fontFamily: 'Microsoft YaHei, Arial Black, Arial, sans-serif',
      fontSize: '26px', color: '#ffea00', fontStyle: 'bold',
    }).setOrigin(0.5).setDepth(62);

    this.add.text(cx, cy + 10, `你的新分数 ${this.analysis.totalScore} 刷新了个人最佳！`, {
      fontFamily: 'Microsoft YaHei, Arial, sans-serif',
      fontSize: '14px', color: '#ffffff',
    }).setOrigin(0.5).setDepth(62);

    const viewBtn = this.add.container(cx - 100, cy + 75, [
      this.add.rectangle(0, 0, 200, 44, COLORS.yellow).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, 200, 44, 0x000000).setOrigin(0.5).setStrokeStyle(2, COLORS.yellow),
      this.add.text(0, 0, '立即查看', {
        fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '18px', color: '#ffea00', fontStyle: 'bold',
      }).setOrigin(0.5),
    ]).setDepth(62).setSize(200, 44).setInteractive({ useHandCursor: true });

    viewBtn.on('pointerover', () => (viewBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    viewBtn.on('pointerout', () => (viewBtn.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    viewBtn.on('pointerdown', () => {
      soundManager.play('ui_click');
      overlay.destroy(); panel.destroy(); glow.destroy(); star.destroy();
      this.fadeTo('LeaderboardScene', { promoted: true });
    });

    const closeBtn = this.add.text(cx + 100, cy + 75, '继续', {
      fontFamily: 'Microsoft YaHei, sans-serif', fontSize: '16px', color: '#555577',
    }).setOrigin(0.5).setDepth(62).setInteractive({ useHandCursor: true });
    closeBtn.on('pointerdown', () => {
      soundManager.play('ui_click');
      overlay.destroy(); panel.destroy(); glow.destroy(); star.destroy();
      viewBtn.destroy(); closeBtn.destroy();
    });

    const popup = [overlay, glow, panel, star, viewBtn, closeBtn];
    popup.forEach(o => o.setAlpha(0));
    this.tweens.add({ targets: popup, alpha: 1, duration: 250, ease: 'Power2' });
  }

  private makeBtn(x: number, y: number, label: string, color: number, cb: () => void) {
    const c = this.add.container(x, y, [
      this.add.rectangle(0, 0, 240, 44, color).setOrigin(0.5).setAlpha(0.2),
      this.add.rectangle(0, 0, 240, 44, 0x000000).setOrigin(0.5).setStrokeStyle(2, color),
      this.add.text(0, 0, label, {
        fontFamily: 'Arial', fontSize: '18px', color: intToHex(color), fontStyle: 'bold',
      }).setOrigin(0.5),
    ]).setDepth(10).setAlpha(0);
    c.setSize(240, 44).setInteractive({ useHandCursor: true });
    c.on('pointerover', () => (c.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.4));
    c.on('pointerout', () => (c.list[0] as Phaser.GameObjects.Rectangle).setAlpha(0.2));
    c.on('pointerdown', () => { soundManager.play('ui_click'); cb(); });
    this.tweens.add({ targets: c, alpha: 1, duration: 500, delay: 1000 });
  }

  private fadeTo(scene: string, data?: any) {
    this.cameras.main.fadeOut(500, 0, 0, 0);
    this.time.delayedCall(500, () => this.scene.start(scene, data));
  }

  private gradeColor(g: string): string {
    return g === 'S+' || g === 'S' ? '#ffea00' : g === 'A' ? '#00f0ff' : g === 'B' ? '#00e676' : g === 'C' ? '#ff9100' : '#ff1744';
  }
  private statColor(v: number): string {
    return v >= 80 ? '#00e676' : v >= 60 ? '#00f0ff' : v >= 40 ? '#ffea00' : '#ff1744';
  }
  private statColorNum(v: number): number {
    if (v >= 80) return COLORS.green;
    if (v >= 60) return COLORS.cyan;
    if (v >= 40) return COLORS.yellow;
    return COLORS.red;
  }
}
